import { useEffect, useRef, useState } from 'react';
import { readOAuthMode, takePendingUnlock } from '../lib/authAccount';
import { useSignedIn } from './useSignedIn';
import { useEntitlement } from './useEntitlement';

// L'autre bout de la marque posee par le verrou avant l'etape compte (voir
// markPendingUnlock). Le retour Google recharge l'app : rien de l'ecran d'avant
// ne survit, et sans ce pont l'utilisateur revient connecte sur l'ecran normal,
// persuade que son achat n'a servi a rien.
//
// On rouvre le verrou, et lui seul. Pas la caisse : personne ne doit voir un
// paiement partir d'un rechargement qu'il n'a pas demande.
//
// Rend le contexte du verrou a rouvrir, ou null. Une seule fois par lancement.

// La marque ne se lit qu'une fois par session de webview. Au niveau module, donc
// partagee par tous les montages : StrictMode monte deux fois, et le second
// montage ne doit pas trouver le stockage deja vide et rester muet.
// null = pas encore lue, false = lue et rien a rouvrir.
let taken = null;

function readOnce() {
  if (taken === null) {
    // L'aller-retour OAuth n'est pas fini : ce chargement-ci porte encore le code
    // dans l'URL et va se faire remplacer par le reload de fin d'echange
    // (useOAuthReturn). Consommer la marque maintenant, c'est la perdre juste
    // avant le boot qui en a besoin. Le mode se lit forcement a temps :
    // completeOAuthReturn ne l'efface qu'apres l'echange, qui ne demarre qu'au
    // rendu suivant.
    taken = (readOAuthMode() === null ? takePendingUnlock() : null) || false;
  }
  return taken;
}

// Sortie de test uniquement : les modules gardent leur etat entre deux tests.
export function _resetPendingUnlock() {
  taken = null;
}

export function usePendingUnlock() {
  const { user, signedIn } = useSignedIn();
  const { fullApp, patreonAccess, betaAccess } = useEntitlement();
  const [context, setContext] = useState(null);
  const settled = useRef(false);

  useEffect(() => {
    if (settled.current) return;
    const pending = readOnce();
    if (!pending) {
      settled.current = true;
      return;
    }
    // La session n'est pas encore lue : user reste null jusqu'a la reponse de
    // getSessionUser, y compris pour un invite, qui a pourtant un user anonyme.
    // C'est le seul signal qui separe "pas connecte" de "pas encore su", et sans
    // lui on trancherait sur un faux "pas connecte" au premier rendu.
    if (!user) return;
    settled.current = true;
    // Deux facons de n'avoir rien a rouvrir : le retour a echoue et il n'y a pas
    // de compte au bout du detour, ou la personne a deja tout et on n'a rien a
    // lui vendre. La marque a ete consommee, on n'en reparle plus.
    if (!signedIn || fullApp || patreonAccess || betaAccess) return;
    setContext(pending);
  }, [user, signedIn, fullApp, patreonAccess, betaAccess]);

  return context;
}

export default usePendingUnlock;
